import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { MainLayout } from './components/Layout';
import Login from './pages/Login';
// import Register from './pages/Register'; // Public registration disabled
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Tenants from './pages/Tenants';
import Rooms from './pages/Rooms';
import Invoices from './pages/Invoices';
import Payments from './pages/Payments';
import Bills from './pages/Bills';
import Reports from './pages/Reports';
import Deposits from './pages/Deposits';
import Maintenance from './pages/Maintenance';
import Transfers from './pages/Transfers';
import Messaging from './pages/Messaging';
import WaterReadings from './pages/WaterReadings';
import {
  TenantDashboard,
  TenantInvoices,
  TenantPayments,
  TenantBills,
  TenantDeposits,
  TenantMaintenance,
  TenantTransfers,
  TenantWaterReadings,
} from './pages/Tenant';
import authService from './services/authService';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminLandlords from './pages/Admin/AdminLandlords';
import AdminWallet from './pages/Admin/AdminWallet';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Protected Route Component
const PrivateRoute = ({ children }) => {
  return authService.isAuthenticated() ? children : <Navigate to="/login" />;
};

// Admin Route Component - Only accessible by superuser and landlord
const AdminRoute = ({ children }) => {
  const user = authService.getCurrentUser();
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" />;
  }
  if (user?.role === 'tenant') {
    return <Navigate to="/tenant/dashboard" replace />;
  }
  if (user?.role === 'caretaker') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Staff Route Component - Accessible by superuser, landlord, and caretaker
const StaffRoute = ({ children }) => {
  const user = authService.getCurrentUser();
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" />;
  }
  if (user?.role === 'tenant') {
    return <Navigate to="/tenant/dashboard" replace />;
  }
  return children;
};

// SuperAdmin Route - Only accessible by superuser
const SuperAdminRoute = ({ children }) => {
  const user = authService.getCurrentUser();
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" />;
  }
  if (user?.role !== 'superuser') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Tenant Route Component - Only accessible by tenants
const TenantRoute = ({ children }) => {
  const user = authService.getCurrentUser();
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" />;
  }
  if (user?.role !== 'tenant') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Role-based Default Route
const DefaultRoute = () => {
  const user = authService.getCurrentUser();
  if (user?.role === 'tenant') {
    return <Navigate to="/tenant/dashboard" replace />;
  }
  if (user?.role === 'superuser') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route
            element={
              <PrivateRoute>
                <MainLayout />
              </PrivateRoute>
            }
          >
            {/* Staff Routes (superuser, landlord, caretaker) */}
            <Route path="/dashboard" element={<StaffRoute><Dashboard /></StaffRoute>} />
            <Route path="/tenants" element={<StaffRoute><Tenants /></StaffRoute>} />
            <Route path="/rooms" element={<StaffRoute><Rooms /></StaffRoute>} />
            <Route path="/invoices" element={<StaffRoute><Invoices /></StaffRoute>} />
            <Route path="/payments" element={<StaffRoute><Payments /></StaffRoute>} />
            <Route path="/bills" element={<StaffRoute><Bills /></StaffRoute>} />
            <Route path="/deposits" element={<StaffRoute><Deposits /></StaffRoute>} />
            <Route path="/maintenance" element={<StaffRoute><Maintenance /></StaffRoute>} />
            <Route path="/transfers" element={<StaffRoute><Transfers /></StaffRoute>} />
            <Route path="/reports" element={<StaffRoute><Reports /></StaffRoute>} />
            <Route path="/water-readings" element={<StaffRoute><WaterReadings /></StaffRoute>} />
            {/* Admin-only Routes */}
            <Route path="/messaging" element={<AdminRoute><Messaging /></AdminRoute>} />
            <Route path="/settings" element={<Settings />} />

            {/* SuperAdmin Routes */}
            <Route path="/admin/dashboard" element={<SuperAdminRoute><AdminDashboard /></SuperAdminRoute>} />
            <Route path="/admin/landlords" element={<SuperAdminRoute><AdminLandlords /></SuperAdminRoute>} />
            <Route path="/admin/wallet" element={<SuperAdminRoute><AdminWallet /></SuperAdminRoute>} />
            
            {/* Tenant Routes */}
            <Route path="/tenant/dashboard" element={<TenantRoute><TenantDashboard /></TenantRoute>} />
            <Route path="/tenant/invoices" element={<TenantRoute><TenantInvoices /></TenantRoute>} />
            <Route path="/tenant/payments" element={<TenantRoute><TenantPayments /></TenantRoute>} />
            <Route path="/tenant/bills" element={<TenantRoute><TenantBills /></TenantRoute>} />
            <Route path="/tenant/deposits" element={<TenantRoute><TenantDeposits /></TenantRoute>} />
            <Route path="/tenant/maintenance" element={<TenantRoute><TenantMaintenance /></TenantRoute>} />
            <Route path="/tenant/transfers" element={<TenantRoute><TenantTransfers /></TenantRoute>} />
            <Route path="/tenant/water-readings" element={<TenantRoute><TenantWaterReadings /></TenantRoute>} />
          </Route>
          <Route path="/" element={<DefaultRoute />} />
        </Routes>
      </Router>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </ThemeProvider>
  );
}

export default App;
